import { useContext, useEffect, useMemo, useState } from "react";
import type { SystemMessage } from "@/models.ts";
import {
  useSystemMessages,
  useAddSystemMessage,
  useEditSystemMessage,
  useDeleteSystemMessage,
  useRestoreSystemMessage,
} from "@/queries";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { deriveMessageStatus } from "../MessageStatus";

export default function useSystemMessagesAdmin() {
  const messagesQuery = useSystemMessages(true, true);
  const addMutation = useAddSystemMessage();
  const editMutation = useEditSystemMessage();
  const deleteMutation = useDeleteSystemMessage();
  const restoreMutation = useRestoreSystemMessage();
  const { showToast } = useContext(FileSubmissionsContext);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<
    SystemMessage | undefined
  >(undefined);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const messages = useMemo(
    () => messagesQuery.data ?? [],
    [messagesQuery.data]
  );

  const inactive = useMemo(
    () => messages.filter((m) => m.active === false),
    [messages]
  );

  const { past, liveScheduled } = useMemo(() => {
    const active = messages.filter((m) => m.active !== false);
    return {
      past: active.filter((m) => deriveMessageStatus(m, now) === "Past"),
      liveScheduled: active.filter(
        (m) => deriveMessageStatus(m, now) !== "Past"
      ),
    };
  }, [messages, now]);

  const error =
    messagesQuery.error ??
    addMutation.error ??
    editMutation.error ??
    deleteMutation.error ??
    restoreMutation.error;

  const handleEdit = (message: SystemMessage) => {
    setEditingMessage(message);
    setPopupOpen(true);
  };
  const handleNewMessage = () => {
    setEditingMessage(undefined);
    setPopupOpen(true);
  };
  const handlePopupClose = () => {
    setPopupOpen(false);
    setEditingMessage(undefined);
  };
  const handleDelete = (message: SystemMessage) => {
    deleteMutation.mutate(message, {
      onSuccess: () => {
        showToast(
          "success",
          "Message Deleted",
          `"${message.title}" has been deleted. It is still viewable in the Inactive tab.`
        );
      },
    });
  };
  const handleRestore = (message: SystemMessage) => {
    restoreMutation.mutate(message, {
      onSuccess: () => {
        showToast(
          "success",
          "Message Restored",
          `"${message.title}" has been restored.`
        );
      },
    });
  };
  const handleSubmit = (message: SystemMessage) => {
    const isEditing = !!editingMessage;
    const mutation = isEditing ? editMutation : addMutation;
    mutation.mutate(message, {
      onSuccess: (savedMessage) => {
        handlePopupClose();
        if (isEditing) return;
        const status = deriveMessageStatus(savedMessage, new Date());
        if (status === "Live") {
          showToast(
            "success",
            "Message Live",
            `"${savedMessage.title}" is now live on user dashboards.`
          );
        } else if (status === "Scheduled") {
          showToast(
            "success",
            "Message Scheduled",
            `"${savedMessage.title}" will go live at its scheduled start time.`
          );
        } else {
          showToast(
            "warning",
            "Message Created (Already Expired)",
            `"${savedMessage.title}" was created with a display window in the past and will not appear on any dashboard.`
          );
        }
      },
    });
  };

  return {
    messages,
    past,
    liveScheduled,
    inactive,
    now,
    isLoading: messagesQuery.isLoading,
    saving: addMutation.isPending || editMutation.isPending,
    error,
    popupOpen,
    editingMessage,
    handleEdit,
    handleDelete,
    handleRestore,
    handleNewMessage,
    handlePopupClose,
    handleSubmit,
  };
}
